import { DeviceIds, deviceType } from '@/core/environment/deviceType';
import { SplitModal } from '@/core/modal';
import { usePriceService } from '@/layout/appWrapper/ServiceProvider';
import businessCover from '@/public/images/covers/upgrade-cover.webp';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { trackEvent } from '@/core/monitoring/trackEvents';
import { useTracker } from '@/core/tracking';
import { OnboardingModal } from '@/layout/menuActions';
import { useLocalization } from '@/services/localizationService';
import { PaymentStrategiesIds } from '@/services/paymentService';
import { Price, PricePeriod } from '@/services/priceService';
import { UserAction, useUser } from '@/services/userService';
import { isLoggedInSelector } from '@/stores/authStore';
import { closeModalSelector, pushModalSelector } from '@/stores/modalStore';
import {
  isFreeTrialEnabledState,
  isReminderEnabledState,
  onboardingState,
  selectedPaymentStrategyState,
} from '@/stores/onboardingStore';
import settingsState from '@/stores/settingsStore';
import Spinner from '@/ui/content/Spinner';
import { paywallTriggerActionIds } from '../../../onboarding/paywallTriggerAction';
import BusinessSubscriptionBenefits from '../../BusinessSubscriptionBenefits';
import { HighlightedPaymentCard } from '../../highlightedPayment';
import OfferPageModal, {
  OfferPageSelection,
} from '../../offerPage/OfferPageModal';
import { getPaywallOfferPageVariant } from '../../offerPage/paywallOfferPageVariant';
import usePaymentStrategies from '../../paymentStrategies/usePaymentStrategies';
import OtherPlansCaption from './OtherPlansCaption';

export default function PaymentSelectionModal({
  id,
  onClose,
  initialOfferPageSelection,
  previousAction,
}: {
  id: string;
  onClose: () => void;
  initialOfferPageSelection?: OfferPageSelection;
  previousAction?: UserAction;
}) {
  const ref = useRef({ el: null, scrolled: false });
  const isReturningToOfferPageRef = useRef(false);
  const { t, data } = useLocalization();
  const closeModal = useSetRecoilState(closeModalSelector);
  const { usePaywallPrices, useFormatRecurringPrice } = usePriceService();
  const paywallPrices = usePaywallPrices();
  const settings = useRecoilValue(settingsState);
  const [price, setPrice] = useState<Price | undefined>(undefined);
  const isDesktop = useMemo(() => deviceType() === DeviceIds.Desktop, []);
  const { track } = useTracker();
  const pushModal = useSetRecoilState(pushModalSelector);
  const offerPageVariant = getPaywallOfferPageVariant(
    settings.values.paywallOfferPageVariant
  );

  useEffect(() => {
    if (paywallPrices) {
      setPrice(paywallPrices.business.weekly);
    }
  }, [paywallPrices]);

  const formattedPrice = useFormatRecurringPrice(price);
  const { data: user, isLoading: isUserLoading } = useUser();
  const isLoggedIn = useRecoilValue(isLoggedInSelector);
  const isFreeTrialAvailable =
    user?.monetization?.trialAvailable && settings.values.isFreeTrialEnabled;
  const isIntroPriceEnabled =
    user?.monetization?.trialAvailable &&
    !settings.values.isFreeTrialEnabled &&
    settings.values.isIntroPriceEnabled;
  const isOcpOfferPageReady =
    offerPageVariant !== 'ocp' ||
    Boolean(
      settings.values.ocp2wIntroPriceId && user?.monetization?.trialAvailable
    );
  const isOcpOfferPagePending = offerPageVariant === 'ocp' && isUserLoading;

  const setOnboarding = useSetRecoilState(onboardingState);
  const isFreeTrialEnabled = useRecoilValue(isFreeTrialEnabledState);
  const isReminderEnabled = useRecoilValue(isReminderEnabledState);

  const setSelectedPaymentStrategy = useSetRecoilState(
    selectedPaymentStrategyState
  );

  const paymentStrategies = usePaymentStrategies({
    selectedPeriod: 'weekly',
    remotePaymentStrategies: settings.values.paymentStrategies,
    isUpgrade: false,
    userPricePeriod: 'weekly',
  });

  const paymentStrategyToUse =
    paymentStrategies.find(
      (strategy) => strategy.id === PaymentStrategiesIds.Business
    ) || null;

  const onModalClose = useCallback(() => {
    track('paywall_selection', 'dismissed');
    closeModal(id);
  }, [closeModal, id, track]);

  const returnToOfferPage = useCallback(
    (selection?: OfferPageSelection) => {
      pushModal({
        id,
        Modal: PaymentSelectionModal,
        onClose,
        previousAction,
        initialOfferPageSelection: selection,
      });
    },
    [id, onClose, previousAction, pushModal]
  );

  const openSubscriptionFlow = useCallback(
    (
      action: 'highlighted_selected' | 'other_selected',
      loginPreviousAction: string,
      returnCallbacks?: {
        onReturnToOfferPage?: () => void;
        onReturnToOfferPageFromCheckout?: () => void;
        isReturningToOfferPageRef?: typeof isReturningToOfferPageRef;
        initialOfferPageStage?: 'other-plans' | 'checkout';
        initialOfferPageSelection?: OfferPageSelection;
        onRequestClose?: () => void;
      }
    ) => {
      track('paywall_selection', action);
      pushModal({
        id: 'payment',
        Modal: OnboardingModal,
        previousAction: UserAction.Subscription,
        loginPreviousAction,
        stepIndex: isLoggedIn ? 1 : undefined,
        onClose,
        ...returnCallbacks,
      });
      closeModal(id);
    },
    [closeModal, id, isLoggedIn, onClose, pushModal, track]
  );

  const highlightedPaymentData = {
    title: t(data.tiers.business),
    subtitle: t(data.tiers.businessSubtitle),
    billingPeriod: t(data.common.week),
    badgeText: null,
    hasFreeTrial: isFreeTrialAvailable,
    hasIntroPrice: isIntroPriceEnabled,
    selectedPeriod: 'weekly',
  };

  useEffect(() => {
    trackEvent(['paywall', 'shown'], { type: 'force_business' });
  }, [id]);

  if (offerPageVariant !== 'control' && isOcpOfferPagePending) {
    return <Spinner inline={false} />;
  }

  if (
    offerPageVariant !== 'control' &&
    isOcpOfferPageReady &&
    !isOcpOfferPagePending
  ) {
    return (
      <OfferPageModal
        id={id}
        variant={offerPageVariant}
        previousAction={
          previousAction || paywallTriggerActionIds.paywallSelectionHighlighted
        }
        initialSelection={initialOfferPageSelection}
        onContinue={(selection) =>
          openSubscriptionFlow(
            'highlighted_selected',
            paywallTriggerActionIds.paywallSelectionHighlighted,
            {
              initialOfferPageStage: 'checkout',
              initialOfferPageSelection: selection,
              onReturnToOfferPageFromCheckout: () =>
                returnToOfferPage(selection),
              isReturningToOfferPageRef,
              onRequestClose: () => {
                isReturningToOfferPageRef.current = true;
                closeModal('payment');
                returnToOfferPage(selection);
              },
            }
          )
        }
        onOtherPlans={(selection) =>
          openSubscriptionFlow(
            'other_selected',
            paywallTriggerActionIds.paywallSelectionOther,
            {
              initialOfferPageStage: 'other-plans',
              initialOfferPageSelection: selection,
              onReturnToOfferPage: () => returnToOfferPage(selection),
              isReturningToOfferPageRef,
              onRequestClose: () => {
                isReturningToOfferPageRef.current = true;
                closeModal('payment');
                returnToOfferPage(selection);
              },
            }
          )
        }
        onDismiss={onModalClose}
      />
    );
  }

  return (
    <SplitModal
      id={id}
      onClose={onModalClose}
      className="min-h-[420px] max-w-[714px]"
    >
      <SplitModal.LeftPanel
        mobileBackground={businessCover}
        desktopBackground={businessCover}
        rootChildren={
          <div
            className="absolute top-0 start-0 w-full h-full"
            style={{
              backgroundImage:
                'linear-gradient(0deg, rgba(0,0,0,0.5),rgba(0,0,0,0) 68.62%)',
            }}
          />
        }
      >
        {isDesktop && (
          <div className="relative w-full">
            <BusinessSubscriptionBenefits />
          </div>
        )}
      </SplitModal.LeftPanel>
      <SplitModal.RightPanel className="overflow-y-auto" sectionRef={ref}>
        <div className="relative w-full">
          <div className="flex flex-col justify-center items-center space-y-6 w-full">
            <HighlightedPaymentCard
              title={highlightedPaymentData.title}
              subtitle={highlightedPaymentData.subtitle}
              price={formattedPrice}
              billingPeriod={highlightedPaymentData.billingPeriod}
              badgeText={highlightedPaymentData.badgeText}
              hasFreeTrial={highlightedPaymentData.hasFreeTrial}
              hasIntroPrice={highlightedPaymentData.hasIntroPrice}
              onPaymentSelected={() => {
                setSelectedPaymentStrategy(paymentStrategyToUse);
                setOnboarding({
                  isFreeTrialEnabled,
                  isReminderEnabled,
                  selectedPeriod:
                    highlightedPaymentData.selectedPeriod as PricePeriod,
                  selectedPaymentStrategy: paymentStrategyToUse,
                });
                openSubscriptionFlow(
                  'highlighted_selected',
                  paywallTriggerActionIds.paywallSelectionHighlighted
                );
              }}
            />

            <div className="flex items-center w-[100%]">
              <span className="border-t-2 w-[100%] border-gray-100" />
              <p className="mx-2 M16 text-gray-400">or</p>
              <span className="border-t-2 w-[100%] border-gray-100" />
            </div>
            <p className="R16 text-black">
              <OtherPlansCaption />
            </p>
            <button
              type="button"
              className="btn btn--large btn--white w-[100%] !mt-4 shadow-dark-button cursor-pointer"
              onClick={() =>
                openSubscriptionFlow(
                  'other_selected',
                  paywallTriggerActionIds.paywallSelectionOther
                )
              }
            >
              {t(data.onboarding.seeOtherPlans)}
            </button>
          </div>
        </div>
      </SplitModal.RightPanel>
    </SplitModal>
  );
}
